'use client'

import Link from 'next/link'
import { Logo } from '@/components/Logo/Logo'
import { CMSLink } from '@/components/Link'
import React, { ReactNode, useEffect, useState } from 'react'
import { LucideMail, LucidePhone, LucidePin } from 'lucide-react'
import { Button } from '@/components/Button'
import { formatDistance } from 'date-fns'
import { nl } from 'date-fns/locale'
import { PaymentBadges } from '@/components/PaymentBadges'
import type { Footer as FooterType } from '@/payload-types'

type FooterLinkEntry = NonNullable<FooterType['links']>[number]

// Map preserves insertion order so editors control column sequence via the CMS array.
function groupLinksBySection(
  links: FooterLinkEntry[],
): Array<[string, FooterLinkEntry[]]> {
  // Header used when a row has no Sectie set — matches the legacy hard-coded
  // column so docs saved before the field was added render unchanged.
  const FALLBACK_HEADER = 'Links'
  const groups = new Map<string, { header: string; entries: FooterLinkEntry[] }>()
  for (const entry of links) {
    const trimmed = entry.section?.trim()
    const header = trimmed || FALLBACK_HEADER
    // Case-insensitive grouping (locale-aware for Dutch) — "Films" and
    // "films" land in the same column. The first row's casing wins as the
    // displayed header so editors see what they typed.
    const key = header.toLocaleLowerCase('nl-NL')
    const existing = groups.get(key)
    if (existing) {
      existing.entries.push(entry)
    } else {
      groups.set(key, { header, entries: [entry] })
    }
  }
  return Array.from(groups.values()).map(({ header, entries }) => [header, entries])
}

interface FooterClientProps {
  projects: any[]
  coaches: any[]
  films: any[]
  links: FooterLinkEntry[]
  ticketEvents: any[]
  sections?: {
    showCoaches?: boolean | null
    showProjects?: boolean | null
    showTickets?: boolean | null
    showFilms?: boolean | null
    showContact?: boolean | null
    showSocials?: boolean | null
    showOther?: boolean | null
  }
  buildTime?: string
  buildVersion?: string
}

export default function FooterClient({
  projects,
  coaches,
  films,
  links,
  ticketEvents,
  sections = {},
  buildTime,
  buildVersion,
}: FooterClientProps) {
  const {
    showCoaches = true,
    showProjects = true,
    showTickets = true,
    showFilms = true,
    showContact = true,
    showSocials = true,
    showOther = true,
  } = sections
  return (
    <footer className="border-t-4 border-primary bg-black dark:bg-card text-white">
      <div className="container pt-8 pb-8 gap-8 flex flex-col md:flex-row justify-between">
        <div className={'flex flex-col max-w-[380px]'}>
          <DonateWidget />
          <p className={'mt-1'}>Draag bij om onze projecten gratis voor een groep te realiseren!</p>
        </div>
        <div className="columns-1 lg:columns-2 xl:columns-3 xl:max-w-[50%] gap-4 grow">
          {showCoaches && (
            <LinksSection header={'Coaches'}>
              {coaches.map((coach, index) => (
                <div className={'inline'} key={coach.slug ?? 'coach' + index}>
                  <Link
                    href={'/coach/' + coach.slug}
                    className={'inline hover:underline whitespace-nowrap'}
                  >
                    {coach.name}
                  </Link>
                  {index < coaches.length - 1 && <span className={'select-none'}>, </span>}
                </div>
              ))}
            </LinksSection>
          )}
          {showProjects && (
            <LinksSection header={'Projecten'}>
              {projects.map((project) => (
                <a
                  key={project.slug}
                  href={'/project/' + project.slug}
                  className={'block hover:underline'}
                >
                  {project.title}
                </a>
              ))}
            </LinksSection>
          )}
          {showTickets && (
            <LinksSection header={'Tickets'}>
              {ticketEvents.map((event, index) => (
                <a
                  key={event.slug || 'ticket-event-' + index}
                  href={'/tickets/' + event.slug}
                  className={'block hover:underline'}
                >
                  {event.name}
                </a>
              ))}
            </LinksSection>
          )}
          {groupLinksBySection(links).map(([header, sectionLinks]) => (
            <LinksSection key={header} header={header}>
              <div className="[&_a]:text-white [&_a:hover]:text-white">
                {sectionLinks.map((link) => (
                  <CMSLink key={link.id} {...link.link} className={'block hover:underline'} />
                ))}
              </div>
            </LinksSection>
          ))}
          {showFilms && (
            <LinksSection header={'Films'}>
              {films.map((film) => (
                <a key={film.slug} href={'/film/' + film.slug} className={'block hover:underline'}>
                  {film.name}
                </a>
              ))}
            </LinksSection>
          )}
          {showContact && (
            <LinksSection header={'Contact'}>
              <div>
                <LucideMail className={'size-4 inline mr-1 -mt-[2px]'} />
                <a href={'mailto:info@jeugdfilms.nl'} className={'hover:underline'}>
                  info@jeugdfilms.nl
                </a>
              </div>
              <div>
                <LucidePhone className={'size-4 inline mr-1 -mt-[2px]'} />
                <a href={'tel:+31851249688'} className={'hover:underline'}>
                  085-1249688
                </a>
              </div>
              <Link href={'/contact'}>
                <Button size={'sm'} className={'mt-2 md:mt-4'} variant={'outline'}>
                  <LucidePin fill={'currentColor'} className={'size-4 inline mr-2'} />
                  Meeting inplannen
                </Button>
              </Link>
            </LinksSection>
          )}
          {showSocials && (
            <LinksSection header={'Socials'}>
              <a href={'https://www.youtube.com/@jeugdfilms'} className={'block hover:underline'}>
                YouTube
              </a>
              <a
                href={'https://www.instagram.com/jeugdfilms.nl/'}
                className={'block hover:underline'}
              >
                Instagram
              </a>
            </LinksSection>
          )}
          {showOther && (
            <LinksSection header={'Overig'}>
              <a
                href={'/docs/Algemene-voorwaarden.pdf'}
                className={'block hover:underline'}
              >
                Algemene voorwaarden
              </a>
              <a
                href={'/docs/Privacyverklaring.pdf'}
                className={'block hover:underline'}
              >
                Privacyverklaring
              </a>
              <PaymentBadges />
            </LinksSection>
          )}
        </div>
      </div>
      <div className={'flex flex-col items-center justify-center p-8'}>
        <Link className="inline-flex items-center mx-auto my-4" href="/">
          <Logo />
        </Link>
        {buildTime && (
          <div className={'text-xs text-gray-400'}>
            versie: {buildVersion}
            <BuildAge buildTime={buildTime} />
          </div>
        )}
      </div>
    </footer>
  )
}

/**
 * "(laatst geüpdatet: 3 uur geleden)", filled in only in the browser. The footer is part of cached
 * pages, so the server's "now" is whenever the page was cached; computing the age during render
 * made the cached HTML and the browser's first render disagree on every view (React hydration
 * error 418), and React then throws the server HTML away and renders the page again.
 */
function BuildAge({ buildTime }: { buildTime: string }) {
  const [age, setAge] = useState<string | null>(null)
  useEffect(() => {
    setAge(formatDistance(new Date(buildTime), new Date(), { addSuffix: true, locale: nl }))
  }, [buildTime])
  return age ? <> (laatst geüpdatet: {age})</> : null
}

function LinksSection({ header, children }: { header: string; children?: ReactNode }) {
  if (!children || React.Children.count(children) === 0) {
    return <></>
  }
  return (
    <div className={'break-inside-avoid-column mb-4 lg:pr-2 leading-5'}>
      <span className={'block uppercase text-sm font-semibold tracking-wider'}>
        {header}
      </span>
      {children}
    </div>
  )
}

function DonateWidget() {
  // if (process.env.NODE_ENV === 'development') return null
  // return (
  //   <iframe
  //     loading="lazy"
  //     className={'h-[204px] rounded'}
  //     src={
  //       'https://www.gofundme.com/f/xytzme-doneer-voor-filmprojecten-op-scholen/widget/medium?sharesheet=fundraiser sidebar'
  //     }
  //   />
  // )
  return null
}
